import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type KeyboardEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import type { HotkeyInfo, HotkeyOverrides, OverlayMode } from '@/lib/platform/types';
import { captureBinding } from './binding-capture';

export const HOTKEYS_KEY = ['platform', 'hotkeys'] as const;

type Feedback =
  | { kind: 'needsModifier' }
  | { kind: 'duplicate'; mode: OverlayMode }
  | { kind: 'saveError' }
  | null;

/** The user's overrides implied by the current state, with one change applied. */
function overridesWith(info: HotkeyInfo, mode?: OverlayMode, binding?: string): HotkeyOverrides {
  const overrides: HotkeyOverrides = {};
  for (const b of info.bindings) {
    const value = b.mode === mode && binding !== undefined ? binding : b.binding;
    if (value !== b.defaultBinding) overrides[b.mode] = value;
  }
  return overrides;
}

/**
 * PRD VS-050: live bindings with their status, each changeable by pressing
 * the new combination. Keyboard only: Enter on "Modifier" starts listening,
 * the next combination is the binding, Esc cancels without hiding the window.
 */
export function HotkeyBindings({
  info,
  setHotkeys,
}: {
  info: HotkeyInfo;
  setHotkeys: ((overrides: HotkeyOverrides) => Promise<HotkeyInfo>) | undefined;
}) {
  const { t } = useTranslation('settings');
  const queryClient = useQueryClient();
  const [capturing, setCapturing] = useState<OverlayMode | null>(null);
  const [feedback, setFeedback] = useState<Feedback>(null);

  const save = useMutation({
    mutationFn: (overrides: HotkeyOverrides) =>
      setHotkeys ? setHotkeys(overrides) : Promise.reject(new Error('read-only')),
    onSuccess: (fresh) => queryClient.setQueryData(HOTKEYS_KEY, fresh),
    onError: () => setFeedback({ kind: 'saveError' }),
  });

  function onCaptureKey(mode: OverlayMode, event: KeyboardEvent<HTMLButtonElement>): void {
    const result = captureBinding(event);
    if (result.kind === 'ignore') return;
    // Handled here: the window-level Esc listener must not hide the window.
    event.preventDefault();
    if (result.kind === 'cancel') {
      setCapturing(null);
      setFeedback(null);
      return;
    }
    if (result.kind === 'needsModifier') {
      setFeedback({ kind: 'needsModifier' });
      return;
    }
    const clash = info.bindings.find((b) => b.mode !== mode && b.binding === result.binding);
    if (clash) {
      setFeedback({ kind: 'duplicate', mode: clash.mode });
      return;
    }
    setCapturing(null);
    setFeedback(null);
    save.mutate(overridesWith(info, mode, result.binding));
  }

  const customised = info.bindings.some((b) => b.binding !== b.defaultBinding);

  return (
    <div className="space-y-3">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b">
            <th scope="col" className="py-2 pr-4 font-medium">
              {t('hotkeys.binding')}
            </th>
            <th scope="col" className="py-2 pr-4 font-medium">
              {t('hotkeys.action')}
            </th>
            <th scope="col" className="py-2 pr-4 font-medium">
              {t('hotkeys.status')}
            </th>
            {setHotkeys && (
              <th scope="col" className="py-2 font-medium">
                <span className="sr-only">{t('hotkeys.change')}</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {info.bindings.map((b) => {
            const action = t(`hotkeys.modes.${b.mode}`);
            const listening = capturing === b.mode;
            return (
              <tr key={b.mode} className="border-b last:border-0">
                <td className="py-2 pr-4">
                  <kbd className="rounded-sm border bg-muted px-1.5 py-0.5 font-mono text-xs">
                    {b.binding}
                  </kbd>
                  {b.binding !== b.defaultBinding && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      {t('hotkeys.custom')}
                    </span>
                  )}
                </td>
                <td className="py-2 pr-4">{action}</td>
                <td className={b.registered ? 'py-2 pr-4' : 'py-2 pr-4 text-destructive'}>
                  {b.registered ? t('hotkeys.active') : t('hotkeys.conflict')}
                </td>
                {setHotkeys && (
                  <td className="py-2">
                    <Button
                      size="sm"
                      variant={listening ? 'default' : 'outline'}
                      aria-label={listening ? undefined : t('hotkeys.editLabel', { action })}
                      aria-pressed={listening}
                      disabled={save.isPending}
                      onClick={() => {
                        setFeedback(null);
                        setCapturing(listening ? null : b.mode);
                      }}
                      onKeyDown={listening ? (event) => onCaptureKey(b.mode, event) : undefined}
                      onBlur={() => listening && setCapturing(null)}
                    >
                      {listening ? t('hotkeys.capturing') : t('hotkeys.edit')}
                    </Button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>

      <output aria-live="polite" className="block text-sm">
        {save.isPending && (
          <span className="flex items-center gap-2 text-muted-foreground">
            <Spinner />
            {t('hotkeys.saving')}
          </span>
        )}
        {feedback?.kind === 'needsModifier' && t('hotkeys.needsModifier')}
        {feedback?.kind === 'duplicate' &&
          t('hotkeys.duplicate', { action: t(`hotkeys.modes.${feedback.mode}`) })}
      </output>
      {feedback?.kind === 'saveError' && (
        <Alert variant="destructive" role="alert">
          {t('hotkeys.saveError')}
        </Alert>
      )}

      {setHotkeys && customised && (
        <Button
          size="sm"
          variant="outline"
          disabled={save.isPending}
          onClick={() => {
            setFeedback(null);
            save.mutate({});
          }}
        >
          {t('hotkeys.reset')}
        </Button>
      )}
    </div>
  );
}
